import React, { ReactNode } from 'react';
import { Button, Row, Col, Card, Table } from 'react-bootstrap';
import { router } from '@inertiajs/react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import AuthedLayout from '../../Layouts/Authed';

interface OverallStats {
	total: number;
	correct: number;
	remapped: number;
	excluded: number;
}

interface ConfidenceBucket {
	bucket: string;
	total: number;
	correct: number;
}

interface SourceStats {
	source_system: string;
	total: number;
	correct: number;
}

interface WeekStats {
	week: string;
	total: number;
	correct: number;
	accuracy: number;
}

interface Props {
	accuracyStats: {
		overall: OverallStats;
		byConfidence: ConfidenceBucket[];
		bySource: SourceStats[];
		overTime: WeekStats[];
	};
}

const pct = (n: number, d: number): string => {
	if (d === 0) return '—';
	return `${Math.round((n / d) * 100)}%`;
};

const AdminHome = (props: Props) => {
	const { overall, byConfidence, bySource, overTime } = props.accuracyStats;
	const hasData = overall.total > 0;

	return (
		<div>
			<Row className="mb-4">
				<Col>
					<h2>Welcome!</h2>
				</Col>
			</Row>
			<Row className="mb-4">
				<Col>
					<Button
						variant="primary"
						onClick={() => router.get('/valuation-form/basics')}
					>
						Begin New Valuation
					</Button>
				</Col>
			</Row>

			<Row className="g-4">
				<Col lg={4}>
					<Card>
						<Card.Header className="fw-semibold">AI Parsing Accuracy</Card.Header>
						<Card.Body>
							{!hasData ? (
								<p className="text-muted mb-0">No data yet. Accuracy stats will appear here once P&L uploads have been reviewed.</p>
							) : (
								<>
									<div className="text-center mb-3">
										<div className="display-4 fw-bold">{pct(overall.correct, overall.total)}</div>
										<small className="text-muted">Overall Accuracy</small>
									</div>
									<Table size="sm" borderless className="mb-0">
										<tbody>
											<tr>
												<td className="text-muted">Total items reviewed</td>
												<td className="text-end fw-semibold">{overall.total}</td>
											</tr>
											<tr>
												<td className="text-muted">Correct</td>
												<td className="text-end fw-semibold text-success">{overall.correct}</td>
											</tr>
											<tr>
												<td className="text-muted">Corrected</td>
												<td className="text-end fw-semibold text-danger">{overall.remapped}</td>
											</tr>
											<tr>
												<td className="text-muted">Excluded</td>
												<td className="text-end fw-semibold">{overall.excluded}</td>
											</tr>
										</tbody>
									</Table>
								</>
							)}
						</Card.Body>
					</Card>
				</Col>

				{hasData && byConfidence.length > 0 && (
					<Col lg={4}>
						<Card>
							<Card.Header className="fw-semibold">Accuracy by Confidence</Card.Header>
							<Card.Body>
								<Table striped borderless hover responsive className="mb-0">
									<thead>
										<tr>
											<th>Confidence</th>
											<th className="text-end">Items</th>
											<th className="text-end">Accuracy</th>
										</tr>
									</thead>
									<tbody>
										{byConfidence.map((row) => (
											<tr key={row.bucket}>
												<td>{row.bucket}</td>
												<td className="text-end">{row.total}</td>
												<td className="text-end fw-semibold">{pct(row.correct, row.total)}</td>
											</tr>
										))}
									</tbody>
								</Table>
							</Card.Body>
						</Card>
					</Col>
				)}

				{hasData && bySource.length > 0 && (
					<Col lg={4}>
						<Card>
							<Card.Header className="fw-semibold">Accuracy by Source System</Card.Header>
							<Card.Body>
								<Table striped borderless hover responsive className="mb-0">
									<thead>
										<tr>
											<th>Source</th>
											<th className="text-end">Items</th>
											<th className="text-end">Accuracy</th>
										</tr>
									</thead>
									<tbody>
										{bySource.map((row) => (
											<tr key={row.source_system}>
												<td>{row.source_system}</td>
												<td className="text-end">{row.total}</td>
												<td className="text-end fw-semibold">{pct(row.correct, row.total)}</td>
											</tr>
										))}
									</tbody>
								</Table>
							</Card.Body>
						</Card>
					</Col>
				)}
			</Row>

			{hasData && overTime.length > 1 && (
				<Row className="mt-4">
					<Col>
						<Card>
							<Card.Header className="fw-semibold">Accuracy Over Time</Card.Header>
							<Card.Body>
								<ResponsiveContainer width="100%" height={300}>
									<LineChart data={overTime}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="week" />
										<YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
										<Tooltip formatter={(value) => [`${value}%`, 'Accuracy']} />
										<Line
											type="monotone"
											dataKey="accuracy"
											stroke="#198754"
											strokeWidth={2}
											dot={{ r: 4 }}
										/>
									</LineChart>
								</ResponsiveContainer>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			)}
		</div>
	);
};

AdminHome.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default AdminHome;
